import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    lng: "en-GB", // Explicitly set language instead of using detector
    fallbackLng: "en-GB",
    supportedLngs: ["en-GB", "en-US"],
    load: "currentOnly", // Only load current language, not all variants
    preload: ["en-GB"], // Preload the default language
    defaultNS: "common",
    ns: ["common", "navigation", "marketing"], // Load critical namespaces initially
    // All other namespaces will be lazy-loaded when needed
    fallbackNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      requestOptions: {
        cache: "default", // Use browser cache for better performance
        mode: "cors",
      },
      crossDomain: false,
      // Custom request function for better error handling and performance
      request: (
        options: { requestOptions?: Record<string, unknown> },
        url: string,
        payload: unknown,
        callback: (
          error: Error | null,
          response: { status: number; data: unknown },
        ) => void,
      ) => {
        // payload parameter required by i18next-http-backend but not used in GET requests
        void payload;

        fetch(url, {
          ...options.requestOptions,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        })
          .then((response) => {
            if (!response.ok) {
              if (import.meta.env.DEV) {
                console.error(
                  `Failed to load translation file: ${url}`,
                  response.status,
                  response.statusText,
                );
              }
              throw new Error(
                `HTTP ${response.status}: ${response.statusText}`,
              );
            }
            return response.json();
          })
          .then((data) => {
            callback(null, { status: 200, data });
          })
          .catch((error) => {
            if (import.meta.env.DEV) {
              console.error(
                `Error loading translation file from ${url}:`,
                error,
              );
            }
            callback(error as Error, { status: 500, data: null });
          });
      },
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    react: {
      useSuspense: true, // Enable suspense to wait for translations to load
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added",
      transEmptyNodeValue: "",
      transSupportBasicHtmlNodes: true,
    },
    // Partition namespaces for lazy loading
    partialBundledLanguages: true,
    debug: false, // Disable debug now that issue is resolved
    saveMissing: import.meta.env.DEV, // Log missing keys in development
    missingKeyHandler: (_lngs, ns, key) => {
      if (import.meta.env.DEV) {
        console.warn(`Missing translation key: ${key} in namespace: ${ns}`);
      }
    },
    // Add fallback for missing translations
    parseMissingKeyHandler: (key) => {
      if (import.meta.env.DEV) {
        console.error(`Failed to load translation key: ${key}`);
      }
      return key; // Return the key itself as fallback
    },
  });

// Add error handler for backend loading failures (dev only)
if (import.meta.env.DEV) {
  i18n.on("failedLoading", (lng: string, ns: string, msg: string) => {
    console.error(`[i18n] Failed to load ${lng}/${ns}: ${msg}`);
  });
}

// Expose i18n to window for browser console testing (dev only)
if (import.meta.env.DEV) {
  (window as unknown as Window & { i18n: typeof i18n }).i18n = i18n;
}

export default i18n;
